import type { WindowDescriptor, WindowSectionPlace } from '@krp/client-runtime'

import { ONEC_KIND } from '../features/connection-form/model/settings'
import { ONEC_CREATE_DEFAULTS_WINDOWS, createDefaultsWindows } from '../features/create-defaults/windows'
import { ONEC_IMPORT_WINDOWS, importWindows } from '../features/import/windows'
import { ONEC_INVENTORY_WINDOWS, inventoryWindows } from '../features/inventory/windows'
import { ONEC_PRICE_TAG_WINDOWS, priceTagWindows } from '../features/price-tags/windows'
import { ONEC_PRICING_WINDOWS, pricingWindows } from '../features/pricing/windows'
import { ONEC_RECEIPT_WINDOWS, receiptsWindows } from '../features/receipts/windows'
import { ONEC_SHIFT_WINDOWS, shiftsWindows } from '../features/shifts/windows'
import { ONEC_SOURCE_PURPOSES, type OneCSourcePurpose } from '../features/source/model/types'
import { ONEC_STRUCTURE_WINDOWS, structureWindows } from '../features/structure/windows'
import { ONEC_SUPPLY_WINDOWS, suppliesWindows } from '../features/supplies/windows'
import { browseRestored, decodeBrowseIntent } from '../features/products/model/productsSlice'
import { selectBrowseIntent } from '../features/products/model/selectors'
import type { OneCUiState } from '../state'

/**
 * Окна пакета 1С. Каждое окно раздела — ресурсное: rid в адресе и в identity окна, поэтому
 * окна одной и той же сущности в двух базах — два разных окна, и смена базы в
 * одном не перепривязывает другое (D-064). Содержимое — binding приложения.
 */
export const ONEC_WINDOWS = {
  products: 'onec.products',
  product: 'onec.product',
  productCreate: 'onec.product-create',
} as const

/**
 * Разделы меню Platform, куда пакет 1С приносит окна (UI-SECTIONS). Пакет
 * `platform-ui` не импортируется: имена сверяет реестр окон при сборке
 * приложения — раздел без входа роняет её.
 */
const SECTIONS: Readonly<Record<OneCSourcePurpose, string>> = {
  products: 'nomenclature',
  supplies: 'supplies',
  pricing: 'prices',
  inventory: 'inventory',
  priceTags: 'price-tags',
  import: 'import',
  structure: 'structure',
  // RP-FB12: чеки 1С — вкладкой раздела «Чеки» рядом с чеками кабинетов AQSI.
  receipts: 'receipts',
}

/** Чем вкладка раздела занята, если у раздела несколько окон одного вида. */
const TAB_LABELS: Readonly<Record<OneCSourcePurpose, string>> = {
  products: 'Товары',
  supplies: 'Поставки',
  pricing: 'Цены и остатки',
  inventory: 'Инвентаризация',
  priceTags: 'Ценники',
  import: 'Импорт',
  structure: 'Структура',
  receipts: 'Чеки',
}

/** Вкладка раздела: окно раздела для каждой базы 1С с правом раздела. */
function tabOf(purpose: OneCSourcePurpose, label = TAB_LABELS[purpose], order = 10): WindowSectionPlace {
  return {
    id: SECTIONS[purpose],
    // 1С — первым источником раздела: в «Номенклатуре» база учёта идёт раньше кабинетов кассы.
    tab: { resourceKind: ONEC_KIND, capability: ONEC_SOURCE_PURPOSES[purpose].capability, label, order },
  }
}

/**
 * Место каждого окна в разделах — одной таблицей, а не по файлам фич: окно
 * раздела даёт вкладку, карточки и мастера просто относятся к разделу (меню
 * подсвечивает его, пока они открыты).
 */
const SECTION_PLACES: Readonly<Record<string, WindowSectionPlace>> = {
  [ONEC_WINDOWS.products]: tabOf('products'),
  [ONEC_WINDOWS.product]: { id: SECTIONS.products },
  [ONEC_WINDOWS.productCreate]: { id: SECTIONS.products },
  // RP-SYN11: настройка создания карточек базы — из карточки ресурса; относится к номенклатуре.
  [ONEC_CREATE_DEFAULTS_WINDOWS.createDefaults]: { id: SECTIONS.products },
  [ONEC_SUPPLY_WINDOWS.list]: tabOf('supplies'),
  [ONEC_SUPPLY_WINDOWS.create]: { id: SECTIONS.supplies },
  [ONEC_SUPPLY_WINDOWS.fromInvoice]: { id: SECTIONS.supplies },
  [ONEC_SUPPLY_WINDOWS.supply]: { id: SECTIONS.supplies },
  [ONEC_SUPPLY_WINDOWS.receipts]: { id: SECTIONS.supplies },
  // Список цен базы (записи всех видов цен) — единственная вкладка базы в «Ценах». «Цены и остатки» (сверка и остатки по ключам)
  // вкладкой раздела больше не бывает: остатки — не цены (замечание владельца 18.10.2026); окно живёт по своему адресу.
  // RP-FB39: «Цены» базы — виды цен (справочник); цены товаров вида — окно внутри раздела, не вкладка.
  [ONEC_PRICING_WINDOWS.priceTypes]: tabOf('pricing', 'Цены', 10),
  [ONEC_PRICING_WINDOWS.priceList]: { id: SECTIONS.pricing },
  [ONEC_PRICING_WINDOWS.overview]: { id: SECTIONS.pricing },
  [ONEC_INVENTORY_WINDOWS.inventory]: tabOf('inventory'),
  [ONEC_PRICE_TAG_WINDOWS.priceTags]: tabOf('priceTags'),
  [ONEC_IMPORT_WINDOWS.wizard]: tabOf('import'),
  [ONEC_IMPORT_WINDOWS.profiles]: { id: SECTIONS.import },
  [ONEC_IMPORT_WINDOWS.file]: { id: SECTIONS.import },
  [ONEC_STRUCTURE_WINDOWS.structure]: tabOf('structure'),
  [ONEC_RECEIPT_WINDOWS.receipts]: tabOf('receipts'),
  // Сводки — отдельный пункт меню «Статистика по продажам». Права `sales.read` у вида 1С нет (оно только у AQSI): база попадает в
  // выбор по праву чеков 1С, иначе пункт меню не видит ни одной базы.
  [ONEC_RECEIPT_WINDOWS.salesStatistics]: { id: 'sales-statistics', tab: { resourceKind: ONEC_KIND, capability: ONEC_SOURCE_PURPOSES.receipts.capability, label: 'Статистика по продажам', order: 10 } },
  // Раздел «Смены»: кассовые смены базы — по праву чеков 1С (`sales.read` у вида 1С нет).
  [ONEC_SHIFT_WINDOWS.shifts]: { id: 'shifts', tab: { resourceKind: ONEC_KIND, capability: ONEC_SOURCE_PURPOSES.receipts.capability, label: 'Смены', order: 10 } },
}

function placed(descriptor: WindowDescriptor<OneCUiState>): WindowDescriptor<OneCUiState> {
  const section = SECTION_PLACES[descriptor.kind]
  return section === undefined ? descriptor : { ...descriptor, section }
}

const nativeWindows: readonly WindowDescriptor<OneCUiState>[] = [
  {
    kind: ONEC_WINDOWS.products,
    scope: 'resource',
    paths: ['/onec/:rid/products'],
    label: 'Товары 1С',
    // Вкладка помнит, что человек искал в ЭТОЙ базе, — не найденные товары.
    capture: (state, params) => ({ browse: selectBrowseIntent(state.products, params.rid ?? '') ?? null }),
    restore: (snapshot, params) => {
      const intent = decodeBrowseIntent(snapshot.browse)
      return intent === undefined || params.rid === undefined ? [] : [browseRestored({ resourceId: params.rid, intent })]
    },
  },
  {
    kind: ONEC_WINDOWS.product,
    scope: 'resource',
    paths: ['/onec/:rid/products/:key'],
    label: 'Товар 1С',
    size: 'lg',
    source: { id: 'onec', label: '1С' },
  },
  {
    kind: ONEC_WINDOWS.productCreate,
    scope: 'resource',
    paths: ['/onec/:rid/product-create'],
    label: 'Новый товар 1С',
    size: 'md',
    source: { id: 'onec', label: '1С' },
  },
  // UB1b — цены/остатки и импорт накладных
  ...pricingWindows,
  ...importWindows,
  // UB1c — поставки
  ...suppliesWindows,
  // UB1d — инвентаризация и ценники
  ...inventoryWindows,
  ...priceTagWindows,
  // ONEC-STRUCT — структура 1С (организация → подразделение → склад)
  ...structureWindows,
  // RP-FB12 — чеки ККМ и возвраты базы
  ...receiptsWindows,
  // Раздел «Смены» — кассовые смены базы
  ...shiftsWindows,
  // RP-SYN11 — «Создание по умолчанию» базы
  ...createDefaultsWindows,
]

export const onecWindows: readonly WindowDescriptor<OneCUiState>[] = nativeWindows.map(placed)
